import { Component, OnInit, Input, Output, EventEmitter, OnDestroy, inject } from '@angular/core';
import { ControlContainer, FormGroupDirective, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { IconNames } from '@shared/enums/icon-names';
import { MobilePaymentType } from '@sales/enums/mobile-payment-type';
import { SubscriptionBundleOption } from '@sales/modules/mobilephone/models/mobilephone-search-item';
import { PriceType } from '@shared/pipes/price-format.pipe';
import { Subscription } from 'rxjs';
import { MobilePhoneFormControls } from '../../mobilephone-details.component';
import { VoiceSubscriptionOffering } from '@sales/modules/subscription/models/offerings-response.model';
import { ErrorStateOnTouched } from '@app/material/ErrorStateOnTouched';
import { OfferingHelperService } from '@app/sales/services/offering-helper.service';
import { Price } from '@app/shared/models/price.model';
import { HardwareVariant } from '@app/sales/modules/mobilephone/models/mobilephone-variant';
import { select } from '@ngxs/store';
import { OfferingSelectors } from '@app/sales/states/offering/offering.selectors';
import { PriceFormatPipe } from '@app/shared/pipes/price-format.pipe';
import { SortByPipe } from '@sales/modules/mbb/pipes/sort-by.pipe';
import { MatIcon } from '@angular/material/icon';
import { MatOption } from '@angular/material/core';
import { MatTooltip } from '@angular/material/tooltip';
import { MatSelect, MatSelectTrigger } from '@angular/material/select';
import { MatFormField } from '@angular/material/form-field';
import { DealerStateSelectors } from '@app/core/states/dealer/dealer-state.selectors';

@Component({
  selector: 'app-mobile-upfront-voice-selector',
  templateUrl: './mobile-upfront-offering-selector.component.html',
  styleUrls: ['./mobile-upfront-offering-selector.component.scss'],
  viewProviders: [{ provide: ControlContainer, useExisting: FormGroupDirective }],
  imports: [
    MatFormField,
    MatSelect,
    FormsModule,
    ReactiveFormsModule,
    MatSelectTrigger,
    MatTooltip,
    MatOption,
    MatIcon,
    SortByPipe,
    PriceFormatPipe,
  ],
})
export class MobileUpfrontOfferingSelectorComponent implements OnInit, OnDestroy {
  formGroupDirective = inject(FormGroupDirective);
  isRetailPayment = select(DealerStateSelectors.isRetailPayment);

  MobilePaymentType = MobilePaymentType;
  PriceType = PriceType;
  MobilePhoneFormControls = MobilePhoneFormControls;
  IconNames = IconNames;
  getMobilePlanProductFromOffering = OfferingHelperService.getMobilePlanProductFromOffering;
  getVasProductFromOffering = OfferingHelperService.getVasProductFromOffering;

  manualRegistrationOfferings = select(OfferingSelectors.getManualRegistrationOfferings);

  @Input() mobilePhoneVariant: HardwareVariant;
  @Input() isManualRegistration: boolean;
  @Input() allVoiceOfferings: VoiceSubscriptionOffering[];
  @Output() selectedNewOfferingChanged = new EventEmitter();

  subscriptions$: Subscription[] = [];
  matcher = new ErrorStateOnTouched();
  selectedSubscriptionBundleOption: SubscriptionBundleOption;
  selectedManualRegistrationOption: VoiceSubscriptionOffering;

  get selectedBundlePrice(): Price {
    const selectedOffering = this.getVoiceSubscriptionOffering(this.selectedSubscriptionBundleOption);

    return !!this.getVasProductFromOffering(selectedOffering)
      ? this.getMobilePlanProductFromOffering(selectedOffering)?.monthlyPrice?.price
      : this.selectedSubscriptionBundleOption?.subscriptionPriceInformation?.price;
  }

  get minimumTotalPrice(): number {
    const selectedOffering = this.getVoiceSubscriptionOffering(this.selectedSubscriptionBundleOption);
    const vasProductPrice = this.getVasProductFromOffering(selectedOffering)?.monthlyPrice?.price?.includingVAT ?? 0;
    const vasProductBindingPrice = vasProductPrice * this.selectedSubscriptionBundleOption.subscriptionBindingPeriod;

    return this.selectedSubscriptionBundleOption?.minimumTotalPrice?.includingVAT - vasProductBindingPrice;
  }

  get subscriptionBundleOptions(): SubscriptionBundleOption[] {
    return this.mobilePhoneVariant?.paymentInformation?.subscriptionBundleOptions;
  }

  getVoiceSubscriptionOffering(option: SubscriptionBundleOption): VoiceSubscriptionOffering {
    return this.allVoiceOfferings?.find(offering => offering.id === option?.subscriptionOfferingId);
  }

  ngOnInit() {
    this.subscriptions$.push(
      this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).valueChanges.subscribe(offeringId => {
        this.initialize(offeringId);
      })
    );
    this.initialize(this.formGroupDirective.form.get(MobilePhoneFormControls.NewOfferingId).value);
  }

  private initialize(offeringId: string) {
    if (this.isManualRegistration && !this.subscriptionBundleOptions?.length) {
      this.selectedManualRegistrationOption = this.manualRegistrationOfferings()?.find(
        offering => offering.id === offeringId
      );
    } else {
      this.selectedSubscriptionBundleOption = this.subscriptionBundleOptions.find(
        option => option.subscriptionOfferingId === offeringId
      );
    }
    this.selectedNewOfferingChanged.emit(offeringId);
  }

  ngOnDestroy() {
    this.subscriptions$.forEach(s => s.unsubscribe());
  }
}
